import React, { useState, useEffect } from 'react';
import './KairosTooltip.css';
import kairosLogoWithName from '../images/kairos_logo_with_name.png';

interface KairosBrandProps {
  /** Text to display before "Kairos" */
  prefix?: string;
  /** Text to display after "Kairos" */
  suffix?: string;
  /** CSS classes for the container */
  className?: string;
  /** CSS classes for the Kairos text */
  kairosClassName?: string;
  /** Whether to show the tooltip on hover */
  showTooltip?: boolean;
  /** Custom tooltip width */
  tooltipWidth?: string;
  /** Whether to auto-show tooltip for first-time users */
  autoShow?: boolean;
  /** Whether to show logo with text instead of just text */
  showLogo?: boolean;
  /** CSS classes for the logo image */
  logoClassName?: string;
}

/**
 * A reusable brand component that displays "Kairos" with an optional tooltip
 * Can be used in headers, sidebars, footers, or anywhere the brand name appears
 */
export const KairosBrand: React.FC<KairosBrandProps> = ({
  prefix = '',
  suffix = '',
  className = '',
  kairosClassName = 'cursor-pointer hover:text-primary-600 transition-colors',
  showTooltip = true,
  tooltipWidth = '380px',
  autoShow = false,
  showLogo = false,
  logoClassName = 'h-8 w-auto'
}) => {
  const [showKairosTooltip, setShowKairosTooltip] = useState(false);
  const [hasSeenTooltip, setHasSeenTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });

  // Check if user has seen tooltip before
  useEffect(() => {
    const hasSeen = localStorage.getItem('kairos-tooltip-seen');
    if (hasSeen === 'true') {
      setHasSeenTooltip(true);
    }
  }, []);

  // Auto-show tooltip for first-time users
  useEffect(() => {
    if (autoShow && !hasSeenTooltip) {
      const timer = setTimeout(() => {
        // Find the sign-in form frame to position tooltip to its right
        const signInFrame = document.querySelector('.bg-white.py-8.px-4.shadow.sm\\:rounded-lg.sm\\:px-10') || 
                           document.querySelector('[class*="bg-white"][class*="py-8"]') ||
                           document.querySelector('.bg-white');
        
        if (signInFrame) {
          const rect = signInFrame.getBoundingClientRect();
          const tooltipWidth = 350; // Auto-show tooltip width
          const margin = 20; // Space between frame and tooltip
          
          setTooltipPosition({
            top: rect.top + window.scrollY, // Align with top of frame
            left: rect.right + window.scrollX + margin + (tooltipWidth / 2) // To the right of frame
          });
        } else {
          // Fallback to center if frame not found
          setTooltipPosition({
            top: window.innerHeight / 2,
            left: window.innerWidth / 2
          });
        }
        setShowKairosTooltip(true);
      }, 2000); // Show after 2 seconds

      return () => clearTimeout(timer);
    }
  }, [autoShow, hasSeenTooltip]);

  // Handle tooltip close and mark as seen
  const handleKairosTooltipClose = () => {
    setShowKairosTooltip(false);
    setHasSeenTooltip(true);
    localStorage.setItem('kairos-tooltip-seen', 'true');
  };

  // Handle mouse enter for hover-triggered tooltips
  const handleKairosMouseEnter = (e: React.MouseEvent<HTMLElement>) => {
    if (!showTooltip) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const viewportWidth = window.innerWidth;
    const tooltipWidthNum = parseInt(tooltipWidth.replace('px', ''));
    
    // Ensure tooltip doesn't go off screen
    let left = centerX;
    if (left - tooltipWidthNum / 2 < 10) {
      left = tooltipWidthNum / 2 + 10; // 10px margin from left edge
    } else if (left + tooltipWidthNum / 2 > viewportWidth - 10) {
      left = viewportWidth - tooltipWidthNum / 2 - 10; // 10px margin from right edge
    }
    
    setTooltipPosition({
      top: rect.bottom + window.scrollY + 15,
      left: left
    });
    setShowKairosTooltip(true);
  };

  // Handle mouse leave for hover-triggered tooltips
  const handleKairosMouseLeave = () => {
    if (showTooltip) {
      setShowKairosTooltip(false);
    }
  };

  return (
    <>
      <span className={className}>
        {prefix}
        {showLogo ? (
          <img
            src={kairosLogoWithName}
            alt="Kairos"
            className={`${logoClassName} ${kairosClassName}`}
            style={{
              objectFit: 'cover',
              objectPosition: 'center -80px',
              clipPath: 'inset(0 0 100px 0)'
            }}
            onMouseEnter={handleKairosMouseEnter}
            onMouseLeave={handleKairosMouseLeave}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                handleKairosMouseEnter(e as any)
              }
            }}
            role="button"
            tabIndex={0}
          />
        ) : (
          <span
            className={kairosClassName}
            onMouseEnter={handleKairosMouseEnter}
            onMouseLeave={handleKairosMouseLeave}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                handleKairosMouseEnter(e as any)
              }
            }}
            role="button"
            tabIndex={0}
          >
            Kairos
          </span>
        )}
        {suffix}
      </span>
      
      {/* Interactive Kairos Tooltip */}
      {showKairosTooltip && (
        <div 
          className="kairos-tooltip"
          style={{
            top: `${tooltipPosition.top}px`,
            left: `${tooltipPosition.left}px`,
            transform: 'translateX(-50%)',
            pointerEvents: autoShow ? 'auto' : 'none', // Clickable only when auto-shown
            width: autoShow ? '350px' : tooltipWidth // Smaller when auto-shown
          }}
          onClick={autoShow ? handleKairosTooltipClose : undefined}
          onKeyDown={autoShow ? (e) => {
            if (e.key === 'Escape') {
              handleKairosTooltipClose()
            }
          } : undefined}
          role={autoShow ? "dialog" : undefined}
          tabIndex={autoShow ? 0 : undefined}
        >
          <div className="tooltip-arrow"></div>
          <div 
            className="tooltip-content"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <h4 className="tooltip-title">What is Kairos?</h4>
            <div className="tooltip-text">
              <p>
                <strong>Kairos</strong> is an old Greek word for the opportune moment. Not any moment. The one where preparation and timing line up. Ancient writers used it for the instant an archer finds a small opening, or a sailor catches the right wind.
              </p>
              <p>
                That is the heart of modern job search. Roles open. Teams shift. Windows close. Kairos helps you move when the odds are highest. It sources roles, scores fit, and watches for the signals that hiring momentum is real.
              </p>
              <p>
                When a window opens, it tailors your resume to that role so the right story meets the right moment. The name is a promise. Less guessing. Less noise. More well-timed action.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default KairosBrand;